/**
 * AffirmationsAutoPlayer — Auto-plays all affirmations of a theme in random order.
 * Each step lasts exactly STEP_MS (10 s) — audio plays during that window if available.
 * Voice mode: "ludovic" | "mon-choix" | "sans-voix".
 */
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { ArrowLeft, Pause, Play, SkipForward, SkipBack, Volume2, X } from 'lucide-react';

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const MODE_LABELS = {
  'ludovic': '🎙 La voix de Ludovic',
  'sensuelle': '💕 La voix féminine',
  'mon-choix': '🌟 La voix de mon choix',
  'sans-voix': '🤫 Sans voix',
};

export default function AffirmationsAutoPlayer({ theme, clipsMap, voiceMode = 'ludovic', intervalSec = 10, onClose }) {
  const total = theme?.affirmations?.length || 0;
  // STEP_MS = audio playback window + mental repetition silence. Min 3 s to avoid empty steps.
  const STEP_MS = Math.max(3, intervalSec || 10) * 1000;
  // Shuffled order of indices, stable per session
  const order = useMemo(() => shuffle([...Array(total).keys()]), [total, theme?.id]);
  const [step, setStep] = useState(0);
  const [paused, setPaused] = useState(false);
  const [volume, setVolume] = useState(1);
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const apiBase = process.env.REACT_APP_BACKEND_URL;

  const currentIdx = step < total ? order[step] : -1;
  const finished = step >= total;

  const stopAll = useCallback(() => {
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch {}
      audioRef.current = null;
    }
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const goNext = useCallback(() => setStep((s) => Math.min(s + 1, total)), [total]);
  const goPrev = useCallback(() => setStep((s) => Math.max(s - 1, 0)), []);

  // Effect: at each step, start audio (if voiceMode != sans-voix and clip exists) AND a 10s timer
  useEffect(() => {
    stopAll();
    if (paused || finished) return;

    if (voiceMode !== 'sans-voix') {
      const clip = clipsMap?.[`${currentIdx}-${voiceMode}`];
      // Prefer audio_url (MongoDB-backed, resilient) over legacy on-disk url.
      const src = clip?.audio_url || clip?.url;
      if (src) {
        const a = new Audio(`${apiBase}${src}`);
        a.volume = volume;
        a.onerror = () => {};
        audioRef.current = a;
        a.play().catch(() => {});
      }
    }

    timerRef.current = setTimeout(() => {
      goNext();
    }, STEP_MS);

    return stopAll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, paused, voiceMode]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  useEffect(() => () => stopAll(), [stopAll]);

  if (!theme) return null;
  const text = currentIdx >= 0 ? theme.affirmations[currentIdx] : '';
  const bgImage = theme.images?.[step % (theme.images?.length || 1)];

  const restart = () => { stopAll(); setStep(0); setPaused(false); };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center" data-testid="affirmations-auto-player">
      {bgImage && (
        <div className="absolute inset-0 bg-cover bg-center transition-opacity duration-700"
          style={{ backgroundImage: `url(${bgImage})` }} />
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-black/85 backdrop-blur-sm" />

      <div className="relative z-10 w-full h-full max-w-lg mx-auto flex flex-col px-5 py-6">
        <div className="flex items-center justify-between mb-4">
          <button onClick={onClose} className="flex items-center gap-2 text-white/80 hover:text-white text-sm"
            data-testid="auto-player-back">
            <ArrowLeft size={18} /> Retour
          </button>
          <div className="flex items-center gap-2">
            <span className="text-xs px-3 py-1 rounded-full border border-white/20 text-white/80"
              data-testid="auto-player-voice-mode">
              {MODE_LABELS[voiceMode] || voiceMode}
            </span>
            <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/10 text-white/60"
              data-testid="auto-player-close">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center text-center">
          {finished ? (
            <div className="space-y-4 max-w-sm">
              <p className="text-amber-300 text-2xl font-heading">Séance terminée 🌿</p>
              <p className="text-white/70 text-sm">{total} affirmations diffusées. Profitez de cet instant de calme.</p>
              <button onClick={restart}
                className="mt-4 px-5 py-2.5 rounded-full bg-amber-500 text-slate-900 font-semibold hover:bg-amber-400"
                data-testid="auto-player-restart">
                Recommencer la séance
              </button>
            </div>
          ) : (
            <>
              <p className="text-white/40 text-xs uppercase tracking-widest mb-4">
                {step + 1} / {total}
              </p>
              <p className="text-white font-medium leading-relaxed"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 'clamp(1.4rem, 5vw, 2rem)',
                  textShadow: '0 2px 20px rgba(0,0,0,0.7), 0 0 40px rgba(0,0,0,0.5)',
                }}
                data-testid="auto-player-text">
                "{text}"
              </p>
            </>
          )}
        </div>

        {!finished && (
          <>
            <div className="flex items-center gap-2 mb-4">
              <Volume2 className="w-4 h-4 text-white/40" />
              <input type="range" min="0" max="1" step="0.05" value={volume}
                onChange={(e) => setVolume(parseFloat(e.target.value))}
                className="flex-1 accent-amber-400"
                data-testid="auto-player-volume" />
            </div>
            <div className="flex items-center justify-center gap-4">
              <button onClick={goPrev} disabled={step === 0}
                className="p-3 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 transition"
                data-testid="auto-player-prev">
                <SkipBack className="w-5 h-5 text-white" />
              </button>
              <button onClick={() => setPaused((p) => !p)}
                className="p-4 rounded-full bg-amber-500 hover:bg-amber-400 transition shadow-lg shadow-amber-500/30"
                data-testid="auto-player-toggle">
                {paused ? <Play className="w-6 h-6 text-slate-900" /> : <Pause className="w-6 h-6 text-slate-900" />}
              </button>
              <button onClick={goNext} disabled={step + 1 >= total}
                className="p-3 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 transition"
                data-testid="auto-player-next">
                <SkipForward className="w-5 h-5 text-white" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
